import Spinner from 'components/UI/V3/Spinner/Spinner';
import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import SenderFormModal from './SenderFormModal';
import UseCuddlesDefaultConfirmModal from './UseCuddlesDefaultConfirmModal';
import { useEmailSenderData } from './hooks/useEmailSenderData';
import FailedState from './states/FailedState';
import PendingState from './states/PendingState';
import UnconfiguredState from './states/UnconfiguredState';
import VerifiedState from './states/VerifiedState';
import styles from './EmailSenderComponent.module.scss';

/**
 *   Standalone mode (Email Marketing tab — `EmailSenderHost`):
 *     All optional props are omitted. The component just renders its state
 *     machine; the user navigates away by switching tabs.
 *
 *   Embedded mode (drawer / modal opened from the campaign wizard):
 *     - `embedded={true}`     : drops the standalone outer padding and hides
 *       the SectionHeader's title via CSS custom properties — the hosting
 *       chrome (e.g. DrawerHeader) already provides those.
 *     - `onVerified()`        : called once when the sender transitions to
 *       `verified`. Wizard parent typically closes the drawer so the user
 *       lands back on Step 2 with the newly verified address available.
 *     - `onExit()`            : called when the user wants to leave (e.g. a
 *       Close button in the modal header). Standalone callers can ignore.
 */
export interface EmailSenderComponentProps {
    embedded?: boolean;
    onVerified?: () => void;
    onExit?: () => void;
}

type FormModalMode = 'setup' | 'edit';

/**
 * Email Sender feature core. Renders the state machine over the slice data
 * (Unconfigured / Pending / Verified / Failed) and overlays modals for the
 * setup / edit flow and the "use Cuddles default" escape confirmation.
 *
 * State machine notes:
 *   - `formModal` is `null | 'setup' | 'edit'`. The underlying state UI is
 *     ALWAYS visible; the modal sits on top of it as a portal. This matches
 *     the designer's mockups (mockups 2 & 5) where the modal dims the state
 *     panel behind it instead of replacing it.
 *   - `escapeConfirmOpen` was previously local to FormState; it's lifted up
 *     here so any state (Pending / Verified / Failed) can trigger it via the
 *     "Don't want a custom sender?" link.
 *   - `onVerified` fires exactly once per transition into verified, guarded
 *     by `prevVerifiedRef`. Manual refreshes that keep the status verified
 *     don't re-fire it.
 *   - The modal closes when the slice's `status` or `enabled` changes — the
 *     useEffect below resets `formModal` on data churn so the modal vanishes
 *     after a successful save without each child having to know.
 */
const EmailSenderComponent: FunctionComponent<EmailSenderComponentProps> = ({ embedded = false, onVerified }) => {
    const { data, saving, isUnconfigured, isPending, isVerified, isFailed, fetchSender, updateSender } =
        useEmailSenderData();

    const [formModal, setFormModal] = useState<FormModalMode | null>(null);
    const [escapeConfirmOpen, setEscapeConfirmOpen] = useState<boolean>(false);
    // Gates the unconfigured-state flash on first mount: with no data yet and
    // the slice's `loading` still false, the derived `isUnconfigured` would be
    // true for one frame and render the "Set up custom sender" CTA before the
    // spinner. Local in-flight flag covers that gap.
    const [initialFetchInFlight, setInitialFetchInFlight] = useState<boolean>(true);
    const prevVerifiedRef = useRef<boolean>(isVerified);

    useEffect(() => {
        setInitialFetchInFlight(true);
        fetchSender().finally(() => setInitialFetchInFlight(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close the modals whenever the underlying status/enabled changes — a
    // successful save flips us to pending/verified/failed and the modal
    // should disappear without children having to call back.
    useEffect(() => {
        setFormModal(null);
        setEscapeConfirmOpen(false);
    }, [data?.status, data?.enabled]);

    // Fire onVerified exactly once when crossing into the verified state.
    useEffect(() => {
        if (isVerified && !prevVerifiedRef.current) {
            onVerified?.();
        }
        prevVerifiedRef.current = isVerified;
    }, [isVerified, onVerified]);

    const handleOpenSetup = () => setFormModal('setup');
    const handleOpenEdit = () => setFormModal('edit');
    const handleOpenEscape = () => setEscapeConfirmOpen(true);
    const handleCloseFormModal = () => setFormModal(null);
    const handleCloseEscape = () => setEscapeConfirmOpen(false);

    const handleConfirmEscape = async () => {
        const result = await updateSender({ enabled: false });
        if (result) setEscapeConfirmOpen(false);
    };

    const renderState = (): ReactNode => {
        if (initialFetchInFlight) {
            return (
                <div className={styles.loaderContainer}>
                    <Spinner />
                </div>
            );
        }

        if (isUnconfigured) {
            return <UnconfiguredState onSetup={handleOpenSetup} />;
        }
        if (isPending && data) {
            return (
                <PendingState
                    sender={data}
                    onEdit={handleOpenEdit}
                    onEscape={handleOpenEscape}
                />
            );
        }
        if (isVerified && data) {
            return (
                <VerifiedState
                    sender={data}
                    onEdit={handleOpenEdit}
                    onEscape={handleOpenEscape}
                />
            );
        }
        if (isFailed && data) {
            return (
                <FailedState
                    sender={data}
                    onEdit={handleOpenEdit}
                    onEscape={handleOpenEscape}
                />
            );
        }

        // Defensive — should be unreachable. Falling through to unconfigured is
        // safer than a blank screen if the slice ever lands in an unexpected shape.
        return <UnconfiguredState onSetup={handleOpenSetup} />;
    };

    // Initial email passed into the setup/edit modal.
    //   - `setup` always starts blank (even if the slice still holds a stale
    //     `sender_email` from a previously disabled sender — see the bugfix
    //     in the BE notes: `disable!` doesn't clear the email column).
    //   - `edit` prefills from the currently enabled address.
    const formModalInitialEmail = formModal === 'edit' && data?.enabled ? data.senderEmail ?? '' : '';

    const overlays = (
        <>
            {formModal !== null && (
                <SenderFormModal
                    mode={formModal}
                    initialEmail={formModalInitialEmail}
                    onCancel={handleCloseFormModal}
                    onSubmitted={handleCloseFormModal}
                />
            )}
            {escapeConfirmOpen && (
                <UseCuddlesDefaultConfirmModal
                    saving={saving}
                    onCancel={handleCloseEscape}
                    onConfirm={handleConfirmEscape}
                />
            )}
        </>
    );

    const stateContent = renderState();

    // Standalone renders without an extra wrapper so children inherit the
    // browser-level CSS custom property fallbacks defined in their own
    // stylesheets. Embedded wraps with `.embeddedRoot` which overrides those
    // fallbacks to 0 padding + hidden title.
    return embedded ? (
        <div className={styles.embeddedRoot}>
            {stateContent}
            {overlays}
        </div>
    ) : (
        <>
            {stateContent}
            {overlays}
        </>
    );
};

export default EmailSenderComponent;
